import {
  Badge,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  getCreditFacilityExpenseDetailsTableRetrieveQueryKey,
  getCreditFacilityInterestExpenseEventsRetrieveQueryKey,
  useCreditFacilityInterestExpenseCreate,
} from "gen/endpoints/credit-facility/credit-facility";
import { useFundFamilyEventCreate } from "gen/endpoints/fund-family/fund-family";
import { FundFamilyEventData } from "gen/model/fundFamilyEventData";
import { FundFamilyEventSubTypeEnum } from "gen/model/fundFamilyEventSubTypeEnum";
import { FundFamilyEventTypeEnum } from "gen/model/fundFamilyEventTypeEnum";
import { fundFamilyEventCreateBody } from "gen/schemas/fund-family/fund-family";
import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormCalendarInput } from "shared/components/input/date/CalendarInput";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { nowAsIsoDate } from "shared/utils/DateUtils";
import { setFormValueIfChanged } from "shared/utils/FormUtils";
import { useBoolean } from "usehooks-ts";

interface SharedModalContentProps {
  close: () => void;
}

const DrawdownModalContent: React.FC<SharedModalContentProps> = ({ close }) => {
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();

  const form = useForm<FundFamilyEventData>({
    resolver: zodResolver(fundFamilyEventCreateBody),
    defaultValues: {
      fund_family_id: fundFamilyId,
      type: FundFamilyEventTypeEnum.credit_facility,
      effective_date: nowAsIsoDate(),
      posted_date: nowAsIsoDate(),
      credit_facility_id: creditFacilityId,
      sub_type: FundFamilyEventSubTypeEnum.drawdown,
    },
  });

  const effectiveDate = form.watch("effective_date");
  useEffect(() => {
    setFormValueIfChanged(form, "posted_date", effectiveDate);
  }, [form, effectiveDate]);

  const { mutateAsync: createFundFamilyEvent } = useFundFamilyEventCreate();
  const onSubmit = async (data: FundFamilyEventData) =>
    await createFundFamilyEvent({
      fundFamilyId,
      data,
    });

  const cancelRef = useRef(null);

  const navigate = useNavigate();
  const { t } = useTranslation(["creditFacility", "common"]);
  const creditFacilityRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .creditFacilities({})
    .creditFacility({ creditFacilityId });

  return (
    <ModalContent>
      <ModalHeader>{t("creditFacility:eventModal.runDrawdown")}</ModalHeader>
      <Form
        form={form}
        onSubmit={onSubmit}
        onSuccess={(event: FundFamilyEventData) => {
          navigate(
            creditFacilityRoute
              .event({ fundFamilyEventId: event.id! })
              .drawdown({})
              .update({}).$,
          );
          close();
        }}
      >
        <ModalBody>
          <FormDateInput
            name="effective_date"
            title={t("creditFacility:eventModal.effectiveDate")}
          />
          <FormDateInput
            name="posted_date"
            title={t("creditFacility:eventModal.noticeDate")}
          />
          <FormTextArea
            name="notes"
            title={t("creditFacility:eventModal.notes")}
          />
        </ModalBody>
        <ModalFooter gap={2}>
          <ButtonOrder
            dismiss={<FormCancelButton onClick={close} ref={cancelRef} />}
            confirm={
              <FormSubmitButton>
                {t("creditFacility:eventModal.runDrawdown")}
              </FormSubmitButton>
            }
          />
        </ModalFooter>
      </Form>
    </ModalContent>
  );
};

const PaydownModalContent: React.FC<SharedModalContentProps> = ({ close }) => {
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();

  const form = useForm<FundFamilyEventData>({
    resolver: zodResolver(fundFamilyEventCreateBody),
    defaultValues: {
      fund_family_id: fundFamilyId,
      type: FundFamilyEventTypeEnum.credit_facility,
      effective_date: nowAsIsoDate(),
      posted_date: nowAsIsoDate(),
      credit_facility_id: creditFacilityId,
      sub_type: FundFamilyEventSubTypeEnum.paydown,
    },
  });

  const effectiveDate = form.watch("effective_date");
  useEffect(() => {
    setFormValueIfChanged(form, "posted_date", effectiveDate);
  }, [form, effectiveDate]);

  const { mutateAsync: createFundFamilyEvent } = useFundFamilyEventCreate();
  const onSubmit = async (data: FundFamilyEventData) =>
    await createFundFamilyEvent({
      fundFamilyId,
      data,
    });

  const cancelRef = useRef(null);

  const navigate = useNavigate();
  const { t } = useTranslation(["creditFacility", "common"]);
  const creditFacilityRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .creditFacilities({})
    .creditFacility({ creditFacilityId });

  return (
    <ModalContent>
      <ModalHeader>{t("creditFacility:eventModal.runPaydown")}</ModalHeader>
      <Form
        form={form}
        onSubmit={onSubmit}
        onSuccess={(event: FundFamilyEventData) => {
          navigate(
            creditFacilityRoute
              .event({ fundFamilyEventId: event.id! })
              .paydown({})
              .update({}).$,
          );
          close();
        }}
      >
        <ModalBody>
          <FormDateInput
            name="effective_date"
            title={t("creditFacility:eventModal.effectiveDate")}
          />
          <FormDateInput
            name="posted_date"
            title={t("creditFacility:eventModal.noticeDate")}
          />
          <FormTextArea
            name="notes"
            title={t("creditFacility:eventModal.notes")}
          />
        </ModalBody>
        <ModalFooter gap={2}>
          <ButtonOrder
            dismiss={<FormCancelButton onClick={close} ref={cancelRef} />}
            confirm={
              <FormSubmitButton>
                {t("creditFacility:eventModal.runPaydown")}
              </FormSubmitButton>
            }
          />
        </ModalFooter>
      </Form>
    </ModalContent>
  );
};

const ReallocationModalContent: React.FC<SharedModalContentProps> = ({
  close,
}) => {
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();

  const form = useForm<FundFamilyEventData>({
    resolver: zodResolver(fundFamilyEventCreateBody),
    defaultValues: {
      fund_family_id: fundFamilyId,
      type: FundFamilyEventTypeEnum.credit_facility,
      effective_date: nowAsIsoDate(),
      posted_date: nowAsIsoDate(),
      credit_facility_id: creditFacilityId,
      sub_type: FundFamilyEventSubTypeEnum.reallocation,
    },
  });

  const effectiveDate = form.watch("effective_date");
  useEffect(() => {
    setFormValueIfChanged(form, "posted_date", effectiveDate);
  }, [form, effectiveDate]);

  const { mutateAsync: createFundFamilyEvent } = useFundFamilyEventCreate();
  const onSubmit = async (data: FundFamilyEventData) =>
    await createFundFamilyEvent({
      fundFamilyId,
      data,
    });

  const cancelRef = useRef(null);

  const navigate = useNavigate();
  const { t } = useTranslation(["creditFacility", "common"]);
  const creditFacilityRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .creditFacilities({})
    .creditFacility({ creditFacilityId });

  return (
    <ModalContent>
      <ModalHeader>
        {t("creditFacility:eventModal.runReallocation")}
      </ModalHeader>
      <Form
        form={form}
        onSubmit={onSubmit}
        onSuccess={(event: FundFamilyEventData) => {
          navigate(
            creditFacilityRoute
              .event({ fundFamilyEventId: event.id! })
              .reallocation({})
              .select({}).$,
          );
          close();
        }}
      >
        <ModalBody>
          <FormDateInput
            name="effective_date"
            title={t("creditFacility:eventModal.effectiveDate")}
          />
          <FormTextArea
            name="notes"
            title={t("creditFacility:eventModal.notes")}
          />
        </ModalBody>
        <ModalFooter gap={2}>
          <ButtonOrder
            dismiss={<FormCancelButton onClick={close} ref={cancelRef} />}
            confirm={
              <FormSubmitButton>
                {t("creditFacility:eventModal.selectTranche")}
              </FormSubmitButton>
            }
          />
        </ModalFooter>
      </Form>
    </ModalContent>
  );
};

const MergeTrancheModalContent: React.FC<SharedModalContentProps> = ({
  close,
}) => {
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();

  const form = useForm<FundFamilyEventData>({
    resolver: zodResolver(fundFamilyEventCreateBody),
    defaultValues: {
      fund_family_id: fundFamilyId,
      type: FundFamilyEventTypeEnum.credit_facility,
      effective_date: nowAsIsoDate(),
      posted_date: nowAsIsoDate(),
      credit_facility_id: creditFacilityId,
      sub_type: FundFamilyEventSubTypeEnum.merge_tranches,
    },
  });

  const effectiveDate = form.watch("effective_date");
  useEffect(() => {
    setFormValueIfChanged(form, "posted_date", effectiveDate);
  }, [form, effectiveDate]);

  const { mutateAsync: createFundFamilyEvent } = useFundFamilyEventCreate();
  const onSubmit = async (data: FundFamilyEventData) =>
    await createFundFamilyEvent({
      fundFamilyId,
      data,
    });

  const cancelRef = useRef(null);

  const navigate = useNavigate();
  const { t } = useTranslation(["creditFacility", "common"]);
  const creditFacilityRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .creditFacilities({})
    .creditFacility({ creditFacilityId });

  return (
    <ModalContent>
      <ModalHeader>{t("creditFacility:eventModal.mergeTranches")}</ModalHeader>
      <Form
        form={form}
        onSubmit={onSubmit}
        onSuccess={(event: FundFamilyEventData) => {
          navigate(
            creditFacilityRoute
              .event({ fundFamilyEventId: event.id! })
              .mergeTranches({})
              .creditFacilitiesCreditFacilityEventMergeTranche({}).$,
          );
          close();
        }}
      >
        <ModalBody>
          <Text textStyle="small" color="gray.600" mb="5px">
            {t("creditFacility:eventModal.mergeTrancheDescription")}
          </Text>
          <FormDateInput
            name="effective_date"
            title={t("creditFacility:eventModal.effectiveDate")}
          />
          <FormTextArea
            name="notes"
            title={t("creditFacility:eventModal.notes")}
          />
        </ModalBody>
        <ModalFooter gap={2}>
          <ButtonOrder
            dismiss={<FormCancelButton onClick={close} ref={cancelRef} />}
            confirm={
              <FormSubmitButton>
                {t("creditFacility:eventModal.selectTranche")}
              </FormSubmitButton>
            }
          />
        </ModalFooter>
      </Form>
    </ModalContent>
  );
};

const ExpenseModalContent: React.FC<SharedModalContentProps> = ({ close }) => {
  const queryClient = useQueryClient();
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();
  const form = useForm<FundFamilyEventData>({
    resolver: zodResolver(fundFamilyEventCreateBody),
    defaultValues: {
      fund_family_id: fundFamilyId,
      type: FundFamilyEventTypeEnum.credit_facility,
      effective_date: nowAsIsoDate(),
      posted_date: nowAsIsoDate(),
      credit_facility_id: creditFacilityId,
      sub_type: FundFamilyEventSubTypeEnum.post_interest,
    },
  });
  const { mutateAsync: generateInterestExpenses } =
    useCreditFacilityInterestExpenseCreate();

  const onSubmitInterestExpense = async (data: FundFamilyEventData) => {
    const fundFamilyEventData = await generateInterestExpenses({
      fundFamilyId,
      creditFacilityId,
      data,
    });
    await queryClient.invalidateQueries({
      queryKey: getCreditFacilityInterestExpenseEventsRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
    });
    await queryClient.invalidateQueries({
      queryKey: getCreditFacilityExpenseDetailsTableRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
    });
    return fundFamilyEventData;
  };
  const navigate = useNavigate();
  const { t } = useTranslation(["creditFacility", "common"]);
  const creditFacilityRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .creditFacilities({})
    .creditFacility({ creditFacilityId });

  return (
    <ModalContent>
      <Form
        form={form}
        onSubmit={onSubmitInterestExpense}
        onSuccess={(event: FundFamilyEventData) => {
          navigate(
            creditFacilityRoute
              .event({ fundFamilyEventId: event.id! })
              .expenses({})
              .post({}).$,
          );
          close();
        }}
      >
        <ModalBody display="flex" flexDirection="row" p="0px">
          <VStack flexBasis="100%" alignItems="flex-start" p="16px">
            <HStack>
              <Text fontSize={14} fontWeight={500}>
                {t("creditFacility:expenseEventModal.postingPeriodStartDate")}
              </Text>
              <Badge>
                {t("creditFacility:expenseEventModal.startDateBadge")}
              </Badge>
            </HStack>
            <Text>
              {t("creditFacility:expenseEventModal.startDateDescription")}
            </Text>
          </VStack>
          <Divider
            orientation="vertical"
            borderColor="gray.300"
            height="inherit"
          />
          <VStack flexBasis="100%" p="16px" pb="0px" gap="0px">
            <HStack alignSelf="flex-start">
              <Text fontSize={14} fontWeight={500}>
                {t("creditFacility:expenseEventModal.postingPeriodEndDate")}
              </Text>
              <Badge>
                {t("creditFacility:expenseEventModal.endDateBadge")}
              </Badge>
            </HStack>
            <FormCalendarInput name="effective_date" />
          </VStack>
        </ModalBody>
        <Divider borderColor="gray.300" />
        <ModalFooter gap={2} p="16px">
          <ButtonOrder
            dismiss={<FormCancelButton onClick={close} />}
            confirm={
              <FormSubmitButton> {t("common:confirm")}</FormSubmitButton>
            }
          />
        </ModalFooter>
      </Form>
    </ModalContent>
  );
};

export type AllowedCreditFacilityFundFamilyEventSubTypes =
  | "post_interest"
  | "drawdown"
  | "paydown"
  | "reallocation"
  | "merge_tranches";

type SelectedSubTypeMapping = {
  [K in AllowedCreditFacilityFundFamilyEventSubTypes]: React.FC<SharedModalContentProps>;
};

interface AddCapitalEventModalProps {
  onCloseComplete: () => void;
  eventSubType: AllowedCreditFacilityFundFamilyEventSubTypes;
}

const EXPENSE_SUB_EVENT_TYPES: FundFamilyEventSubTypeEnum[] = [
  FundFamilyEventSubTypeEnum.post_interest,
];

const SUB_TYPE_TO_COMPONENT: SelectedSubTypeMapping = {
  [FundFamilyEventSubTypeEnum.post_interest]: ExpenseModalContent,
  [FundFamilyEventSubTypeEnum.drawdown]: DrawdownModalContent,
  [FundFamilyEventSubTypeEnum.paydown]: PaydownModalContent,
  [FundFamilyEventSubTypeEnum.reallocation]: ReallocationModalContent,
  [FundFamilyEventSubTypeEnum.merge_tranches]: MergeTrancheModalContent,
};

const AddCreditFacilityEventModal: React.FC<AddCapitalEventModalProps> = ({
  onCloseComplete,
  eventSubType,
}) => {
  const { value: isOpen, setFalse: close } = useBoolean(true);
  const cancelRef = useRef(null);

  const ModalContentComponent = SUB_TYPE_TO_COMPONENT[eventSubType];

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      initialFocusRef={cancelRef}
      size={EXPENSE_SUB_EVENT_TYPES.includes(eventSubType) ? "2xl" : undefined}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContentComponent close={close} />
    </Modal>
  );
};
export default AddCreditFacilityEventModal;
